import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RootState} from '../../../hooks/store';
import {AvailabilityApiResponseChannel} from '../../../../server/routes/availability/types';
import {getLanguageCode} from '../../../../shared/utils/getHeaderLanguageCode';
import {getUrlInfo} from '../../../utils/getUrlInfo';
import {Status} from '../availability/slice';
import {fetchCachedChannel} from '../../../api/channel';

export type ChannelState = {
  status: Status;
  data: AvailabilityApiResponseChannel;
};

const initialState: ChannelState = {
  status: 'idle',
  data: {
    code: '',
    locale: '',
    type: '',
    languageCode: '',
    currencyCode: '',
    currencyFormat: 'symbol_start',
    country: {countryCode: ''},
    decimalSeparator: '',
    distanceUnit: '',
  },
};

const isRequestPending = (state: RootState) => {
  const status = selectChannelRequestStatus(state);
  return status !== 'pending';
};

export const fetchChannel = createAsyncThunk(
  'channel/fetch',
  ({url}: {url: string}) => {
    const {pathname} = getUrlInfo(url);
    const languageCode = getLanguageCode(pathname);

    return fetchCachedChannel({languageCode});
  },
  {
    condition(_, {getState}) {
      const state = getState() as RootState;
      return isRequestPending(state);
    },
  },
);

export const channelSlice = createSlice({
  name: 'channel',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(fetchChannel.fulfilled, (state, action) => {
      state.data.locale = action.payload.locale;
      state.data.code = action.payload.code;
      state.data.type = action.payload.type;
      state.data.languageCode = action.payload.languageCode;
      state.data.currencyCode = action.payload.currencyCode;
      state.data.currencyFormat = action.payload.currencyFormat;
      state.data.country = action.payload.country;
      state.data.decimalSeparator = action.payload.decimalSeparator;
      state.data.distanceUnit = action.payload.distanceUnit;
      state.status = 'fulfilled';
    });
    builder.addCase(fetchChannel.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(fetchChannel.rejected, (state) => {
      state.status = 'rejected';
    });
  },
});

const selectChannelRootState = (state: RootState) => state.channel;

export const selectChannelData = (state: RootState) => {
  return selectChannelRootState(state).data;
};

export const selectChannelRequestStatus = (state: RootState) =>
  selectChannelRootState(state).status;

export const selectIsMarketplace = (state: RootState) => {
  return selectChannelData(state).type === 'consumer_site';
};

export const selectIsWidget = (state: RootState) => {
  return selectChannelData(state).type === 'widget';
};

export const selectLanguageCode = (state: RootState) => {
  const channel = selectChannelData(state);
  const [languageCode] = channel.locale && channel.locale.split('_');
  return languageCode;
};
